import { Avatar, IconButton, Tooltip, Typography, type IColumn } from 'shared/ui'
import { DayjsFormats, getLabelAsNumberInternationalFormat } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { EmptyCell } from 'shared/ui/Table'
import { type IClickToTextStatisticsLeads } from 'entities/ClickToText'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { getCellForTags } from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable'
import styles from './styles.module.scss'

export const getColumns = (onOpenContactsDetails: IOpenContactsDetails) => {
  const nameColumn: IColumn<IClickToTextStatisticsLeads> = {
    field: 'name',
    name: 'Name',
    width: '100%',
    maxWidth: '182px',
    renderRowCell: (row, _, config) => {
      if (!row) return null

      return (
        <div className={styles.contactColumn}>
          <div className={styles.contactColumnName}>
            <Avatar info={row.avatarInfo} size={24} />
            <Typography
              variant={'body-md-regular'}
              ellipsis
              tooltipProps={{ label: row.contact.full_name }}
              ariaLabel={'ContactName'}
              color={row.isDeleted ? 'var(--content-primary-disabled)' : 'primary'}
            >
              {row.contact.full_name || '—'}
            </Typography>
          </div>
          <Tooltip label='Conversation' placement='top'>
            <IconButton
              icon='conversation'
              variant='icon'
              color='tertiary'
              transparentInactive
              className={config?.hidden}
              ariaLabel='ContactRow_sendMessage'
              disabled={onOpenContactsDetails.disabled}
              onClick={(event) => {
                event.stopPropagation()
                onOpenContactsDetails.open({
                  contactId: row.contact.id,
                })
              }}
            />
          </Tooltip>
        </div>
      )
    },
  }

  const numberColumn: IColumn<IClickToTextStatisticsLeads> = {
    field: 'number',
    name: 'Phone',
    width: '132px',
    renderRowCell: (row) =>
      row.contact.number ? (
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'ContactNumber'}
          color={row.isDeleted ? 'var(--content-primary-disabled)' : 'primary'}
        >
          {getLabelAsNumberInternationalFormat(row.contact.number)}
        </Typography>
      ) : (
        <EmptyCell />
      ),
  }

  const emailColumn: IColumn<IClickToTextStatisticsLeads> = {
    field: 'email',
    name: 'Email',
    maxWidth: '220px',
    renderRowCell: (row) =>
      row.contact.email ? (
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'ContactEmail'}
          color={row.isDeleted ? 'var(--content-primary-disabled)' : 'primary'}
        >
          {row.contact.email}
        </Typography>
      ) : (
        <EmptyCell />
      ),
  }

  const tagsColumn: IColumn<IClickToTextStatisticsLeads> = {
    field: 'tags',
    name: 'Tags',
    renderRowCell: (row) =>
      row.contact.tags ? (
        <span aria-label='ContactTags'>
          {getCellForTags({
            tags: row.contact.tags,
            isDisabled: row.contact.isDisabled || row.isDeleted,
          })}
        </span>
      ) : (
        <EmptyCell />
      ),
  }

  const sendTimeColumn: IColumn<IClickToTextStatisticsLeads> = {
    field: 'send_time',
    name: 'Send time',
    renderRowCell: (row) =>
      row.sendTime ? (
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'ContactSendTime'}
          color={row.isDeleted ? 'var(--content-primary-disabled)' : 'primary'}
        >
          {uiStore.dayjs(row.sendTime).format(DayjsFormats.fullWithAtDash2)}
        </Typography>
      ) : (
        <EmptyCell />
      ),
  }

  return [nameColumn, numberColumn, emailColumn, tagsColumn, sendTimeColumn]
}
